'use client';

import { MEMBER_ROLES, MEMBER_TYPES } from '@slims/shared/constants';
import { MEMBER_CSV_TEMPLATE, memberCsvRow, parseMemberCsv } from '@slims/shared/validation';
import type { Member, MemberAccess, MemberType } from '@slims/shared/types';
import {
	Bell,
	CheckCircle,
	Circle,
	FileText,
	Loader2,
	Paperclip,
	Pencil,
	Sun,
	Trash2,
	X,
} from 'lucide-react';
import * as React from 'react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../atoms/button/button';
import { BodySkeleton } from '../../molecules/card-skeleton/card-skeleton';
import { ConfirmationDialog } from '../../molecules/confirmation-dialog/confirmation-dialog';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../molecules/dialog/dialog';
import { cn } from '../../lib/cn';

/** Tipo di notifica creabile manualmente o generata dal sistema. */
export type NotificationTipo =
	| 'pod_anomaly'
	| 'monthly_report'
	| 'weekly_summary'
	| 'manual'
	| 'system';

export interface RegistryPod {
	/** Id univoco del POD. */
	id: string;
	/** Codice POD. */
	codice: string;
	/** Id del socio a cui è associato. */
	memberId?: string | null;
	/** Tipo di POD (es. produzione, consumo). */
	tipo: string;
	/** Tecnologia dell'impianto, se di produzione. */
	tecnologia?: string | null;
	/** Potenza installata in kWp, se di produzione. */
	potenzaKwp?: number | null;
	/** Data di entrata in esercizio dell'impianto (ISO). */
	dataEntrataEsercizio?: string | null;
	/** Se true, l'impianto era preesistente alla costituzione della CER. */
	preEsistente?: boolean | null;
	/** Codice Unità di Produzione GSE. */
	codiceUP?: string | null;
	/** Codice GAUDÌ Terna dell'impianto. */
	codiceGaudi?: string | null;
}

export interface RegistryNotificationDeliveryResult {
	delivery?: {
		email: { requested: number; sent: number; skipped: number; failed: number };
	};
}

export interface MembersRegistryViewProps {
	/** Soci della CER da mostrare nel registro. */
	members: Member[];
	/** Se true, i soci sono in caricamento. */
	isLoading: boolean;
	/** Messaggio di errore, se il caricamento è fallito. */
	error: string | null;
	/** Nome della CER, mostrato nelle intestazioni di export/invito. */
	cerNome?: string | null;
	/** Tutti i POD della CER, per l'assegnazione e la validazione dei dati di impianto. */
	allPods: RegistryPod[];
	/** Se true, apre automaticamente il dialog "Aggiungi socio" al montaggio (da `?add=1`). */
	autoOpenAdd: boolean;
	/** Crea un nuovo socio; ritorna il record creato. */
	onAddMember: (payload: Record<string, unknown>) => Promise<Member>;
	/** Elimina un socio. */
	onDeleteMember: (id: string) => Promise<unknown>;
	/** Crea una notifica per uno o più soci. */
	onCreateNotification: (
		payload: Record<string, unknown>,
	) => Promise<RegistryNotificationDeliveryResult>;
	/** Carica un file grezzo su storage; ritorna il suo URL pubblico. Lancia un'eccezione in caso di errore. */
	onUploadToStorage: (file: File, path: string) => Promise<{ publicUrl: string }>;
	/** Naviga a un altro percorso dell'app (es. dettaglio del socio). */
	onNavigate: (path: string) => void;
	/** Validazione di un singolo campo, usata per azzerare l'errore mentre l'utente digita. */
	validateMemberField: (field: string, value: unknown) => boolean;
}

/** Data in formato italiano; `–` quando il valore manca o non è una data valida. */
function formatDate(iso?: string | null): string {
	if (!iso) return '–';
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? '–' : d.toLocaleDateString('it-IT');
}

/** Stile comune dei controlli della barra filtri (select tipo e ricerca). */
const FILTER_CONTROL_STYLE: React.CSSProperties = {
	borderRadius: 6,
	border: '1px solid var(--color-border)',
	background: 'var(--color-card)',
	padding: '4px 10px',
	fontSize: 12,
	color: 'var(--color-text-primary)',
	outline: 'none',
};

// ─── form state ────────────────────────────────────────────────────────────
const EMPTY_FORM = {
	nome: '',
	tipo: 'residential',
	// Vuoto di proposito: il ruolo lo sceglie chi aggiunge il socio.
	ruolo: '',
};

/** Etichetta e colore dello stato di accesso al portale (INB-396). */
const ACCESS_META: Record<MemberAccess, { label: string; badge: string }> = {
	attivo: { label: 'Attivo', badge: 'badge-green' },
	invitato: { label: 'Invitato', badge: 'badge-orange' },
	nessuno: { label: 'Nessuno', badge: 'badge-blue' },
};

/** Riga skeleton della tabella soci: checkbox + una manciata di colonne a larghezza variabile. */
function MemberRowSkeleton() {
	return (
		<tr>
			<td style={{ width: 28 }}>
				<BodySkeleton className="h-3.5 w-3.5 rounded-full" />
			</td>
			<td colSpan={8}>
				<div className="flex items-center gap-6">
					<BodySkeleton className="h-3 w-40" />
					<BodySkeleton className="h-3 w-16" />
					<BodySkeleton className="h-3 w-16" />
					<BodySkeleton className="h-3 w-20" />
					<BodySkeleton className="h-3 w-24" />
					<BodySkeleton className="h-5 w-16 rounded-full" />
				</div>
			</td>
		</tr>
	);
}

/** Skeleton della tabella soci: stessa `.data-table` reale, righe al posto dello spinner centrato. */
function MembersTableSkeleton() {
	return (
		<table
			className="data-table"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico, stesso pattern di PanelCardSkeleton/DistributionBar.
			role="status"
			aria-label="Caricamento…"
		>
			<tbody>
				{Array.from({ length: 8 }).map((_, i) => (
					<MemberRowSkeleton key={i} />
				))}
			</tbody>
		</table>
	);
}

// ─── page ───────────────────────────────────────────────────────────────────
const NOTIFY_FORM_EMPTY = {
	titolo: '',
	messaggio: '',
	tipo: 'manual' as NotificationTipo,
	canale: '',
	allegatoPath: null as string | null,
	allegatoNome: null as string | null,
};

/**
 * Pagina "Registro Soci": elenco dei soci della configurazione con ricerca e filtri,
 * aggiunta rapida (il resto si completa nei Dettagli), import CSV e notifiche mirate.
 */
/** Ordine dei punti di misura nella colonna POD. */
const POD_TIPO_ORDER: readonly string[] = ['withdrawal', 'injection', 'storage'];

const MembersRegistryView = ({
	members,
	isLoading,
	error,
	cerNome,
	allPods,
	autoOpenAdd,
	onAddMember,
	onDeleteMember,
	onCreateNotification,
	onUploadToStorage,
	onNavigate,
	validateMemberField,
}: MembersRegistryViewProps) => {
	const [search, setSearch] = useState('');
	const [addOpen, setAddOpen] = useState(false);

	// Auto-open add dialog when navigating with ?add=1
	useEffect(() => {
		if (autoOpenAdd) {
			setAddOpen(true);
		}
	}, [autoOpenAdd]);
	const [form, setForm] = useState(EMPTY_FORM);
	const [saving, setSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [notifyOpen, setNotifyOpen] = useState(false);
	const [notifyForm, setNotifyForm] = useState(NOTIFY_FORM_EMPTY);
	const [notifySaving, setNotifySaving] = useState(false);

	const notifySelectedMembers = useMemo(() => {
		return members.filter((m: Member) => selectedIds.has(m.id));
	}, [members, selectedIds]);

	const channelStats = useMemo(() => {
		const total = notifySelectedMembers.length;
		if (total === 0) return { email: 0, total };

		return {
			email: notifySelectedMembers.filter((m: Member) => !!m.email).length,
			total,
		};
	}, [notifySelectedMembers]);

	// Filtro per tipo di socio.
	const [tipoFilter, setTipoFilter] = useState<'all' | MemberType>('all');
	const [impiantoOpenId, setImpiantoOpenId] = useState<string | null>(null);

	const [uploadingAttachment, setUploadingAttachment] = useState(false);
	const notifyAttachmentRef = useRef<HTMLInputElement | null>(null);
	const csvImportRef = useRef<HTMLInputElement | null>(null);
	const [importingCSV, setImportingCSV] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<{
		isOpen: boolean;
		title: string;
		description: string;
		onConfirm: () => void;
	}>({
		isOpen: false,
		title: '',
		description: '',
		onConfirm: () => {},
	});

	function confirmDeleteSelected() {
		const ids = [...selectedIds];
		const n = ids.length;
		setConfirmDelete({
			isOpen: true,
			title: n > 1 ? `Elimina ${n} soci` : 'Elimina socio',
			description: `Sei sicuro di voler eliminare ${n > 1 ? `i ${n} soci selezionati` : 'il socio selezionato'}? Tutti i dati associati, inclusi i POD, verranno rimossi permanentemente.`,
			onConfirm: async () => {
				setConfirmDelete((prev) => ({ ...prev, isOpen: false }));
				// Come l'import CSV: un solo toast che passa dall'avanzamento all'esito.
				const toastId = toast.loading(`Eliminazione in corso… 0 di ${n}`);
				const failed: string[] = [];
				for (let i = 0; i < n; i++) {
					toast.loading(`Eliminazione in corso… ${i + 1} di ${n}`, { id: toastId });
					try {
						await onDeleteMember(ids[i]);
					} catch {
						failed.push(ids[i]);
					}
				}
				const deleted = n - failed.length;
				if (failed.length === 0) {
					toast.success(
						deleted > 1 ? `${deleted} soci eliminati` : 'Socio eliminato con successo',
						{
							id: toastId,
						},
					);
				} else {
					toast.error(`${deleted} di ${n} soci eliminati, ${failed.length} non eliminati`, {
						id: toastId,
					});
				}
				// Restano selezionati solo quelli non eliminati, per poter riprovare.
				setSelectedIds(new Set(failed));
			},
		});
	}

	function handleFieldChange(
		field: keyof typeof EMPTY_FORM,
		value: (typeof EMPTY_FORM)[keyof typeof EMPTY_FORM],
	) {
		const processedValue = value;
		setForm((prev) => ({ ...prev, [field]: processedValue }));

		if (fieldErrors[field]) {
			// check if error is resolved
			if (validateMemberField(field, processedValue)) {
				setFieldErrors((prev) => {
					const next = { ...prev };
					delete next[field];
					return next;
				});
			}
		}
	}

	function clearFieldError(field: string) {
		if (fieldErrors[field]) {
			setFieldErrors((prev) => {
				const next = { ...prev };
				delete next[field];
				return next;
			});
		}
	}

	const filtered = members
		.filter((m) => {
			if (!search) return true;
			const q = search.toLowerCase().trim();
			// `nome` è obbligatorio a schema ma non a runtime: senza il `?? ''` un
			// socio senza nome faceva fallire la ricerca con un TypeError (INB-126).
			const matchesName = (m.nome ?? '').toLowerCase().includes(q);
			const matchesEmail = (m.email ?? '').toLowerCase().includes(q);

			return matchesName || matchesEmail;
		})
		.filter((m) => tipoFilter === 'all' || m.tipo === tipoFilter)
		.sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '', 'it'));

	const hasActiveFilter = !!search || tipoFilter !== 'all';

	function resetFilters() {
		setTipoFilter('all');
		setSearch('');
	}

	const allFilteredSelected = filtered.length > 0 && filtered.every((m) => selectedIds.has(m.id));

	// Un socio receduto resta nel registro (con il badge «Receduto»), ma non è
	// più nella configurazione: non si conta fra i soci né fra i POD (INB-373).
	const withdrawnIds = useMemo(
		() => new Set(members.filter((m) => m.stato === 'withdrawn').map((m) => m.id)),
		[members],
	);
	const activePods = useMemo(
		() => allPods.filter((p) => !p.memberId || !withdrawnIds.has(p.memberId)),
		[allPods, withdrawnIds],
	);

	function toggleSelect(id: string) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function toggleSelectAll() {
		if (allFilteredSelected) {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const m of filtered) next.delete(m.id);
				return next;
			});
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const m of filtered) next.add(m.id);
				return next;
			});
		}
	}

	async function handleNotifyAttachmentUpload(file: File) {
		setUploadingAttachment(true);
		try {
			const safeName = file.name.replace(/\s+/g, '_');
			const path = `documents/notifiche/${Date.now()}_${safeName}`;

			const { publicUrl } = await onUploadToStorage(file, path);

			setNotifyForm((f) => ({ ...f, allegatoPath: publicUrl, allegatoNome: file.name }));
			toast.success('Allegato caricato');
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore caricamento allegato');
		} finally {
			setUploadingAttachment(false);
		}
	}

	async function handleImportCSV(file: File) {
		setImportingCSV(true);
		// Un solo toast che cambia stato: prima l'avanzamento, poi l'esito. Un
		// import di decine di righe dura secondi, e senza segnale sembrava fermo.
		const toastId = toast.loading('Importazione CSV in corso…');
		try {
			const rows = parseMemberCsv(await file.text());
			if (rows.length === 0) throw new Error('File CSV vuoto o senza righe dati');

			const created: string[] = [];
			const skipped: string[] = [];
			// Soci creati, ma con una parte del profilo energetico da rivedere.
			const incompleti: string[] = [];

			for (const [idx, raw] of rows.entries()) {
				const riga = `riga ${idx + 2}`;
				toast.loading(`Importazione CSV in corso… ${idx + 1} di ${rows.length}`, { id: toastId });
				const { member, errore, profilo, avvisi } = memberCsvRow(raw);
				const nome = String(member.nome ?? '');
				if (!nome) {
					skipped.push(`${riga} (manca il nome)`);
					continue;
				}
				if (errore) {
					skipped.push(`${riga} (${nome}: ${errore})`);
					continue;
				}

				try {
					const res = (await onAddMember({
						...member,
						stato: 'incomplete',
						...(profilo ? { profiloEnergetico: profilo } : {}),
					})) as Member & { profilo?: { ok: boolean; error?: string } };
					created.push(nome);
					const problemi = [...avvisi];
					if (res.profilo && !res.profilo.ok && res.profilo.error) problemi.push(res.profilo.error);
					if (problemi.length > 0) incompleti.push(`${riga} (${nome}): ${problemi.join('; ')}`);
				} catch {
					skipped.push(`${riga} (${nome})`);
				}
			}

			if (created.length > 0)
				toast.success(`${created.length} soci importati con successo`, { id: toastId });
			else toast.error('Nessun socio importato', { id: toastId });
			if (skipped.length > 0)
				toast.warning(`${skipped.length} righe saltate: ${skipped.join(', ')}`);
			if (incompleti.length > 0)
				toast.warning(`Dati energetici da rivedere — ${incompleti.join(' · ')}`, {
					duration: 15000,
				});
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore importazione CSV', {
				id: toastId,
			});
		} finally {
			setImportingCSV(false);
		}
	}

	function handleDownloadTemplate() {
		// Il BOM fa aprire a Excel il file in UTF-8, con gli accenti giusti.
		const blob = new Blob([`\uFEFF${MEMBER_CSV_TEMPLATE}\n`], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'modello-soci.csv';
		a.click();
		URL.revokeObjectURL(url);
	}

	async function handleSendNotifications() {
		if (selectedIds.size === 0 || !notifyForm.titolo.trim()) return;
		setNotifySaving(true);
		try {
			const results: RegistryNotificationDeliveryResult[] = [];
			for (const memberId of selectedIds) {
				results.push(
					await onCreateNotification({
						memberId,
						direction: 'outbound',
						tipo: notifyForm.tipo,
						titolo: notifyForm.titolo.trim(),
						messaggio: notifyForm.messaggio.trim() || null,
						letto: false,
						canale: notifyForm.canale.trim() || null,
						allegatoPath: notifyForm.allegatoPath,
						allegatoNome: notifyForm.allegatoNome,
						recipientRole: 'cer_user',
						requestId: null,
					}),
				);
			}
			const notDelivered = results.reduce(
				(total, result) =>
					total + (result.delivery?.email.skipped ?? 0) + (result.delivery?.email.failed ?? 0),
				0,
			);
			if (notDelivered > 0) {
				toast.warning(
					`Notifiche salvate. ${notDelivered} invii esterni non eseguiti per contatto, preferenza o configurazione mancante.`,
				);
			} else {
				toast.success(
					`Notifica inviata a ${selectedIds.size} socio${selectedIds.size > 1 ? 'i' : ''}`,
				);
			}
			setNotifyOpen(false);
			setNotifyForm(NOTIFY_FORM_EMPTY);
			setSelectedIds(new Set());
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore invio notifiche');
		} finally {
			setNotifySaving(false);
		}
	}

	// Member editing now happens on the full-page Dettagli Socio route, which
	// replaces the old inline MemberDetailDialog modal.
	function openDetail(member: Member) {
		onNavigate(`/members/${member.id}/details`);
	}

	async function handleAdd(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault();
		setFieldErrors({});

		// Il quick-add chiede nome, tipo e ruolo: il ruolo decide i punti di misura
		// che nascono col socio. Il resto si completa in "Dettagli".
		const nome = form.nome.trim();
		if (nome.length < 3) {
			setFieldErrors({ nome: 'Il nome deve avere almeno 3 caratteri' });
			toast.error('Inserisci il nome del socio (minimo 3 caratteri).');
			return;
		}
		if (!form.ruolo) {
			setFieldErrors({ ruolo: 'Scegli il ruolo del socio' });
			return;
		}

		setSaving(true);
		try {
			const newMember = await onAddMember({
				nome,
				tipo: form.tipo,
				ruolo: form.ruolo,
				stato: 'incomplete',
			});
			toast.success('Socio aggiunto. Completa i dettagli.');
			setAddOpen(false);
			setForm(EMPTY_FORM);
			onNavigate(`/members/${newMember.id}/details`);
		} catch (err: unknown) {
			alert(`Errore nel salvataggio: ${err instanceof Error ? err.message : String(err)}`);
		} finally {
			setSaving(false);
		}
	}

	return (
		<div>
			{/* ── Header ──────────────────────────────────────────────── */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 12,
					flexWrap: 'wrap',
					gap: 8,
				}}
			>
				<div>
					<div style={{ fontSize: 15, fontWeight: 700 }}>
						Registro Soci - {cerNome || 'Configurazione'}
					</div>
					<div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>
						{members.length - withdrawnIds.size} soci attivi
						{withdrawnIds.size > 0 &&
							` · ${withdrawnIds.size} ${withdrawnIds.size === 1 ? 'receduto' : 'receduti'}`}{' '}
						· {activePods.filter((p) => p.tipo === 'withdrawal').length} POD prelievo ·{' '}
						{activePods.filter((p) => p.tipo === 'injection').length} POD produzione
					</div>
				</div>
				<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
					<button
						type="button"
						className="btn btn-cyan"
						style={{ fontSize: 13 }}
						onClick={() => setAddOpen(true)}
					>
						➕ Aggiungi socio
					</button>
					<button
						type="button"
						className="btn btn-outline"
						style={{ fontSize: 13 }}
						onClick={() => csvImportRef.current?.click()}
						disabled={importingCSV}
					>
						{importingCSV ? '…' : '⬇ Import CSV'}
					</button>
					<button
						type="button"
						className="btn btn-outline"
						style={{ fontSize: 13 }}
						onClick={handleDownloadTemplate}
					>
						Modello CSV
					</button>
				</div>
			</div>

			<input
				ref={csvImportRef}
				type="file"
				accept=".csv,text/csv"
				style={{ display: 'none' }}
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleImportCSV(file);
					e.target.value = '';
				}}
			/>

			{/* ── Filters + Search ────────────────────────────────────── */}
			<div
				style={{
					display: 'flex',
					gap: 6,
					marginBottom: 10,
					flexWrap: 'wrap',
					alignItems: 'center',
				}}
			>
				<select
					aria-label="Filtra per tipo di soggetto"
					value={tipoFilter}
					onChange={(e) => setTipoFilter(e.target.value as 'all' | MemberType)}
					style={FILTER_CONTROL_STYLE}
				>
					<option value="all">Tutti i tipi</option>
					{MEMBER_TYPES.map((t) => (
						<option key={t.value} value={t.value}>
							{t.icon} {t.label}
						</option>
					))}
				</select>
				<input
					type="text"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Cerca nome o email..."
					style={{ ...FILTER_CONTROL_STYLE, width: 180, marginLeft: 'auto' }}
				/>
				{selectedIds.size > 0 && (
					<button
						type="button"
						onClick={() => setNotifyOpen(true)}
						style={{
							fontSize: 12,
							padding: '4px 11px',
							borderRadius: 6,
							border: '1px solid var(--color-accent)',
							background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
							color: 'var(--color-accent)',
							cursor: 'pointer',
							fontWeight: 600,
						}}
					>
						🔔 Notifica ({selectedIds.size})
					</button>
				)}
				{selectedIds.size > 0 && (
					<button
						type="button"
						onClick={confirmDeleteSelected}
						style={{
							fontSize: 12,
							padding: '4px 11px',
							borderRadius: 6,
							border: '1px solid var(--color-danger)',
							background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
							color: 'var(--color-danger)',
							cursor: 'pointer',
							fontWeight: 600,
						}}
					>
						Elimina ({selectedIds.size})
					</button>
				)}
			</div>

			{/* ── Table ───────────────────────────────────────────────── */}
			<div className="card" style={{ padding: 0, overflow: 'hidden' }}>
				{isLoading ? (
					<MembersTableSkeleton />
				) : error ? (
					<div
						style={{
							display: 'flex',
							height: 128,
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: 13,
							color: 'var(--color-danger)',
						}}
					>
						{error}
					</div>
				) : (
					<div style={{ overflowX: 'auto' }}>
						<table className="data-table" id="onb-soci-tbl" style={{ minWidth: 960 }}>
							<thead>
								<tr>
									<th style={{ width: 28 }}>
										<span className="sr-only">Seleziona tutti</span>
										<button
											type="button"
											onClick={toggleSelectAll}
											aria-label={allFilteredSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
											style={{ color: 'var(--color-text-muted)' }}
										>
											{allFilteredSelected ? (
												<CheckCircle
													style={{ width: 14, height: 14, color: 'var(--color-accent)' }}
												/>
											) : (
												<Circle style={{ width: 14, height: 14 }} />
											)}
										</button>
									</th>
									<th>Socio</th>
									<th>Tipo</th>
									<th>Ruolo</th>
									<th>POD</th>
									<th>Iscrizione</th>
									<th>Ultimo aggiornamento</th>
									<th title="Accesso al portale: «Attivo» è entrato almeno una volta, «Invitato» ha un account mai usato, «Nessuno» non ha un account - il recupero password non gli arriva, serve «Invita».">
										Accesso
									</th>
									<th>Azioni</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((member) => {
									// Sempre prelievo, immissione, accumulo: nell'ordine di creazione
									// due prosumer mostravano i punti invertiti l'uno rispetto all'altro.
									const memberPods = allPods
										.filter((p) => p.memberId === member.id)
										.sort(
											(a, b) => POD_TIPO_ORDER.indexOf(a.tipo) - POD_TIPO_ORDER.indexOf(b.tipo),
										);
									const injPod = memberPods.find((p) => p.tipo === 'injection');

									const ruoloBadgeClass =
										member.ruolo === 'producer'
											? 'badge-green'
											: member.ruolo === 'prosumer'
												? 'badge-purple'
												: 'badge-blue';

									const ruoloLabel =
										MEMBER_ROLES.find((r) => r.value === member.ruolo)?.label ?? member.ruolo;

									const tipoDef = MEMBER_TYPES.find((t) => t.value === member.tipo);

									return (
										<Fragment key={member.id}>
											<tr onClick={() => openDetail(member)} style={{ cursor: 'pointer' }}>
												<td
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}
												>
													<button
														type="button"
														onClick={() => toggleSelect(member.id)}
														aria-label={
															selectedIds.has(member.id)
																? `Deseleziona ${member.nome}`
																: `Seleziona ${member.nome}`
														}
														style={{ color: 'var(--color-text-muted)' }}
													>
														{selectedIds.has(member.id) ? (
															<CheckCircle className="h-4 w-4 text-accent" />
														) : (
															<Circle className="h-4 w-4" />
														)}
													</button>
												</td>
												<td>
													<strong>{member.nome}</strong>
													{member.email && (
														<>
															<br />
															<span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
																{member.email}
															</span>
														</>
													)}
												</td>
												<td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
													{tipoDef?.label ?? member.tipo}
												</td>
												<td>
													<span className={`tag ${ruoloBadgeClass}`}>{ruoloLabel}</span>
													{(() => {
														const injectionPod = memberPods.find(
															(p) => p.tipo === 'injection' && p.potenzaKwp,
														);
														if (!injectionPod) return null;
														const tecLabel =
															injectionPod.tecnologia === 'fv'
																? 'FV'
																: injectionPod.tecnologia === 'eolico'
																	? 'Eolico'
																	: injectionPod.tecnologia === 'idro'
																		? 'Idroelettrico'
																		: 'FV';
														return (
															<div
																style={{
																	fontSize: 11,
																	color: 'var(--color-accent)',
																	fontFamily: 'var(--font-mono)',
																	marginTop: 3,
																}}
															>
																{injectionPod.potenzaKwp} kWp {tecLabel}
															</div>
														);
													})()}
												</td>
												<td
													style={{
														fontFamily: 'var(--font-mono)',
														fontSize: 12,
													}}
												>
													{memberPods.length > 0 ? (
														memberPods.map((p) => (
															<div key={p.id} style={{ marginBottom: 2 }}>
																<div>{p.codice}</div>
																<div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
																	{p.tipo === 'withdrawal'
																		? 'prelievo'
																		: p.tipo === 'storage'
																			? 'accumulo'
																			: 'immissione'}
																</div>
															</div>
														))
													) : (
														<span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
															-
														</span>
													)}
												</td>
												<td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
													{formatDate(member.dataIscrizione)}
													{member.dataRecesso && (
														<>
															<br />
															<span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
																uscito il {formatDate(member.dataRecesso)}
															</span>
														</>
													)}
												</td>
												<td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
													{formatDate(member.updatedAt)}
												</td>
												<td>
													{member.accesso ? (
														<span className={`tag ${ACCESS_META[member.accesso].badge}`}>
															{ACCESS_META[member.accesso].label}
														</span>
													) : (
														'-'
													)}
												</td>
												<td
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}
												>
													<div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															title="Modifica socio"
															onClick={() => openDetail(member)}
														>
															<Pencil />
														</Button>
														{injPod &&
															(member.ruolo === 'producer' || member.ruolo === 'prosumer') && (
																// Un'icona come matita e cestino: il pulsante testuale
																// stonava solo sulla riga del produttore (INB-375).
																<Button
																	type="button"
																	variant="ghost"
																	size="icon"
																	title="Scheda impianto"
																	aria-label="Scheda impianto"
																	aria-expanded={impiantoOpenId === member.id}
																	onClick={() =>
																		setImpiantoOpenId(
																			impiantoOpenId === member.id ? null : member.id,
																		)
																	}
																>
																	<Sun />
																</Button>
															)}
														<Button
															type="button"
															variant="ghostDanger"
															size="icon"
															title="Elimina socio"
															onClick={() => {
																setConfirmDelete({
																	isOpen: true,
																	title: 'Elimina socio',
																	description: `Sei sicuro di voler eliminare il socio ${member.nome}? Tutti i dati associati, inclusi i POD, verranno rimossi permanentemente.`,
																	onConfirm: async () => {
																		try {
																			await onDeleteMember(member.id);
																			setConfirmDelete((prev) => ({
																				...prev,
																				isOpen: false,
																			}));
																			toast.success('Socio eliminato con successo');
																		} catch (err: unknown) {
																			alert(
																				`Errore eliminazione: ${err instanceof Error ? err.message : String(err)}`,
																			);
																		}
																	},
																});
															}}
														>
															<Trash2 />
														</Button>
													</div>
												</td>
											</tr>
											{impiantoOpenId === member.id && injPod && (
												<tr>
													<td
														colSpan={9}
														style={{
															padding: '12px 16px',
															background:
																'color-mix(in srgb, var(--color-accent) 2.5%, transparent)',
															borderTop:
																'1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
															borderBottom:
																'1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)',
														}}
													>
														{(() => {
															const tecFull =
																injPod.tecnologia === 'fv'
																	? 'Fotovoltaico'
																	: injPod.tecnologia === 'eolico'
																		? 'Eolico'
																		: injPod.tecnologia === 'idro'
																			? 'Idroelettrico'
																			: 'Altro';
															const CUTOFF = '16/12/2021';
															return (
																<>
																	<div
																		style={{
																			fontSize: 11,
																			fontWeight: 700,
																			color: 'var(--color-accent)',
																			textTransform: 'uppercase',
																			letterSpacing: '0.08em',
																			marginBottom: 10,
																		}}
																	>
																		Scheda Impianto - {member.nome.toUpperCase()} (
																		{member.ruolo === 'prosumer' ? 'PROSUMER' : 'PRODUTTORE'})
																	</div>
																	<div
																		style={{
																			display: 'flex',
																			flexWrap: 'nowrap',
																			justifyContent: 'space-between',
																			alignItems: 'center',
																			gap: 8,
																			fontSize: 12,
																			overflowX: 'auto',
																		}}
																	>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				Fonte:{' '}
																			</span>
																			<strong>{tecFull}</strong>
																		</span>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				Potenza:{' '}
																			</span>
																			<strong
																				style={{
																					color: 'var(--color-accent)',
																					fontFamily: 'var(--font-mono)',
																				}}
																			>
																				{injPod.potenzaKwp ?? '-'} kWp
																			</strong>
																		</span>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				Entrata esercizio:{' '}
																			</span>
																			<strong>
																				{injPod.dataEntrataEsercizio
																					? new Date(
																							injPod.dataEntrataEsercizio,
																						).toLocaleDateString('it-IT')
																					: '-'}
																			</strong>
																			{injPod.preEsistente && (
																				<span
																					style={{
																						marginLeft: 4,
																						color: 'var(--color-accent-green)',
																					}}
																				>
																					▶ {CUTOFF} ✓
																				</span>
																			)}
																		</span>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				POD immissione:{' '}
																			</span>
																			<strong
																				style={{
																					fontFamily: 'var(--font-mono)',
																					color: 'var(--color-accent)',
																				}}
																			>
																				{injPod.codice}
																			</strong>
																		</span>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				CENSIMP:{' '}
																			</span>
																			{injPod.codiceUP ? (
																				<>
																					<strong style={{ fontFamily: 'var(--font-mono)' }}>
																						{injPod.codiceUP}
																					</strong>{' '}
																					<span style={{ color: 'var(--color-accent-green)' }}>
																						✓
																					</span>
																				</>
																			) : (
																				<span style={{ color: 'var(--color-text-muted)' }}>
																					N/D
																				</span>
																			)}
																		</span>
																		<span style={{ whiteSpace: 'nowrap' }}>
																			<span style={{ color: 'var(--color-text-muted)' }}>
																				GAUDI Terna:{' '}
																			</span>
																			<strong>{injPod.codiceGaudi ?? 'N/A (no accumulo)'}</strong>
																		</span>
																	</div>
																</>
															);
														})()}
													</td>
												</tr>
											)}
										</Fragment>
									);
								})}
								{filtered.length === 0 && !isLoading && (
									<tr>
										<td
											colSpan={9}
											style={{
												padding: '32px 0',
												textAlign: 'center',
												fontSize: 13,
												color: 'var(--color-text-muted)',
											}}
										>
											{hasActiveFilter ? 'Nessun socio trovato.' : 'Nessun socio registrato.'}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* ── Footer ──────────────────────────────────────────────── */}
			<div
				style={{
					fontSize: 12,
					color: 'var(--color-text-muted)',
					marginTop: 6,
					display: 'flex',
					alignItems: 'center',
					gap: 8,
				}}
			>
				Mostrando {filtered.length} di {members.length} soci.
				{filtered.length < members.length && (
					<button
						type="button"
						className="btn btn-outline"
						style={{ fontSize: 12, padding: '2px 8px' }}
						onClick={resetFilters}
					>
						Carica tutti →
					</button>
				)}
			</div>

			{/* ── Add Member Dialog ─────────────────────────────────────────── */}
			<Dialog open={addOpen} onOpenChange={setAddOpen}>
				<DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-card">
					<DialogHeader>
						<DialogTitle className="text-sm">Aggiungi Socio</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleAdd} className="space-y-3">
						<FormRow label="Nome *" error={fieldErrors.nome}>
							<input
								value={form.nome}
								onChange={(e) => handleFieldChange('nome', e.target.value)}
								onFocus={() => clearFieldError('nome')}
								className={`${INPUT_CLASS} ${fieldErrors.nome ? 'border-danger' : ''}`}
								placeholder="Mario Rossi"
							/>
						</FormRow>
						<div className="grid grid-cols-2 gap-3">
							<FormRow label="Tipo">
								<select
									value={form.tipo}
									onChange={(e) => handleFieldChange('tipo', e.target.value)}
									className={INPUT_CLASS}
								>
									{MEMBER_TYPES.map((t) => (
										<option key={t.value} value={t.value}>
											{t.icon} {t.label}
										</option>
									))}
								</select>
							</FormRow>
							<FormRow label="Ruolo *" error={fieldErrors.ruolo}>
								<select
									value={form.ruolo}
									onChange={(e) => handleFieldChange('ruolo', e.target.value)}
									onFocus={() => clearFieldError('ruolo')}
									className={`${INPUT_CLASS} ${fieldErrors.ruolo ? 'border-danger' : ''}`}
								>
									<option value="" disabled>
										Scegli…
									</option>
									{MEMBER_ROLES.map((r) => (
										<option key={r.value} value={r.value}>
											{r.label}
										</option>
									))}
								</select>
							</FormRow>
						</div>
						<p className="text-[12px] text-text-muted">
							Email, POD, impianto e consumi si completano nei Dettagli del socio.
						</p>
						<DialogFooter>
							<button
								type="button"
								onClick={() => setAddOpen(false)}
								className="rounded-[7px] border border-border bg-card px-4 py-2 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
							>
								Annulla
							</button>
							<button
								type="submit"
								disabled={saving}
								className="inline-flex items-center gap-1.5 rounded-[7px] border border-accent bg-accent/10 px-4 py-2 text-[13px] font-semibold text-accent transition-colors hover:bg-accent/20 disabled:opacity-50"
							>
								{saving && <Loader2 className="h-3 w-3 animate-spin" />}
								Salva Socio
							</button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Member editing moved to the full-page /members/:id/details route. */}

			{/* ── Notify Dialog ─────────────────────────────────────────────── */}

			<Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
				<DialogContent
					className="max-w-md border-border bg-card"
					onInteractOutside={(e) => e.preventDefault()}
					onPointerDownOutside={(e) => e.preventDefault()}
				>
					<DialogHeader>
						<DialogTitle className="text-sm">Invia Notifica</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<p className="rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-text-muted">
							<Bell className="mr-1 inline h-3 w-3 text-accent" />
							{selectedIds.size} socio{selectedIds.size > 1 ? 'i' : ''} selezionato
							{selectedIds.size > 1 ? 'i' : ''}
						</p>
						<FormRow label="Titolo *">
							<input
								value={notifyForm.titolo}
								onChange={(e) => setNotifyForm((f) => ({ ...f, titolo: e.target.value }))}
								className={INPUT_CLASS}
								placeholder="Es. Nuovo impianto in esercizio"
							/>
						</FormRow>
						<FormRow label="Messaggio">
							<textarea
								value={notifyForm.messaggio}
								onChange={(e) => setNotifyForm((f) => ({ ...f, messaggio: e.target.value }))}
								className={`${INPUT_CLASS} min-h-[72px] resize-none`}
								placeholder="Testo della notifica..."
							/>
						</FormRow>
						<div className="grid grid-cols-2 gap-3">
							<FormRow label="Tipo">
								<select
									value={notifyForm.tipo}
									onChange={(e) =>
										setNotifyForm((f) => ({ ...f, tipo: e.target.value as NotificationTipo }))
									}
									className={INPUT_CLASS}
								>
									<option value="manual">Manuale</option>
									<option value="pod_anomaly">Anomalia POD</option>
									<option value="monthly_report">Report mensile</option>
									<option value="weekly_summary">Sommario settimanale</option>
									<option value="system">Sistema</option>
								</select>
							</FormRow>
							<FormRow label="Canale">
								<div className="flex flex-wrap gap-2">
									{(
										[
											{ id: 'system', label: 'Piattaforma', desc: 'Sempre disp.' },
											{
												id: 'email',
												label: 'Email',
												missing: channelStats.total - channelStats.email,
											},
											{
												id: 'email+portale',
												label: 'Tutti',
												missing: channelStats.total - channelStats.email,
											},
										] as { id: string; label: string; desc?: string; missing?: number }[]
									).map((ch) => {
										const isSelected = notifyForm.canale === ch.id;
										const isMissingAny = ch.missing !== undefined && ch.missing > 0;

										return (
											<button
												key={ch.id}
												type="button"
												onClick={() => setNotifyForm((f) => ({ ...f, canale: ch.id }))}
												className={cn(
													'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 transition-all text-left min-w-[100px]',
													isSelected
														? 'border-accent bg-accent/10 border-2'
														: 'border-border bg-card hover:border-accent/50',
												)}
											>
												<span
													className={cn(
														'text-[13px] font-semibold',
														isSelected ? 'text-accent' : 'text-text-primary',
													)}
												>
													{ch.label}
												</span>
												{isMissingAny ? (
													<span className="text-[11px] text-danger">
														Mancante per {ch.missing} soci
													</span>
												) : (
													<span className="text-[11px] text-text-muted">
														{ch.desc || 'Tutti i soci ok'}
													</span>
												)}
											</button>
										);
									})}
								</div>
							</FormRow>
						</div>
						{/* Allegato */}
						<div className="space-y-1">
							<label className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
								Allegato (opzionale)
							</label>
							{notifyForm.allegatoPath ? (
								<div className="flex items-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2">
									<FileText className="h-3.5 w-3.5 shrink-0 text-accent" />
									<span className="flex-1 truncate text-[13px] text-text-primary">
										{notifyForm.allegatoNome}
									</span>
									<button
										type="button"
										onClick={() =>
											setNotifyForm((f) => ({ ...f, allegatoPath: null, allegatoNome: null }))
										}
										className="text-text-muted hover:text-danger"
									>
										<X className="h-3.5 w-3.5" />
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={() => notifyAttachmentRef.current?.click()}
									disabled={uploadingAttachment}
									className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-background px-3 py-2 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
								>
									{uploadingAttachment ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<Paperclip className="h-3.5 w-3.5" />
									)}
									{uploadingAttachment ? 'Caricamento...' : 'Aggiungi allegato'}
								</button>
							)}
							<input
								ref={notifyAttachmentRef}
								type="file"
								className="hidden"
								accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) handleNotifyAttachmentUpload(file);
									e.target.value = '';
								}}
							/>
						</div>
					</div>
					<DialogFooter>
						<button
							type="button"
							onClick={() => setNotifyOpen(false)}
							className="rounded-[7px] border border-border bg-card px-4 py-2 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
						>
							Annulla
						</button>
						<button
							type="button"
							onClick={handleSendNotifications}
							disabled={notifySaving || !notifyForm.titolo.trim()}
							className="inline-flex items-center gap-1.5 rounded-[7px] border border-accent bg-accent/10 px-4 py-2 text-[13px] font-semibold text-accent transition-colors hover:bg-accent/20 disabled:opacity-50"
						>
							{notifySaving && <Loader2 className="h-3 w-3 animate-spin" />}
							Invia a {selectedIds.size} socio{selectedIds.size > 1 ? 'i' : ''}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<ConfirmationDialog
				open={confirmDelete.isOpen}
				onOpenChange={(open) => setConfirmDelete((prev) => ({ ...prev, isOpen: open }))}
				onConfirm={confirmDelete.onConfirm}
				title={confirmDelete.title}
				description={confirmDelete.description}
				variant="destructive"
			/>
		</div>
	);
};

function FormRow({
	label,
	error,
	children,
}: {
	label: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="space-y-1">
			<label className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
				{label}
			</label>
			{children}
			{error && <p className="mt-0.5 text-[12px] font-bold text-danger">{error}</p>}
		</div>
	);
}

const INPUT_CLASS =
	'w-full rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] text-text-primary focus:border-accent focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-card/50';

MembersRegistryView.displayName = 'MembersRegistryView';

export { MembersRegistryView };
